import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap, catchError, withLatestFrom, filter } from 'rxjs/operators';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { of } from 'rxjs';
import {
  onWebsocketMessageDispatchAddRemoveEntities,
  onWebsocketMessageDispatchUpdateEntities
} from '@zerops/zef/entities';
import { NotificationEntity } from './notification-base.entity';
import {
  markAsRead,
  markAsReadFail,
  markAllAsRead,
  markAllAsReadSuccess,
  markAllAsReadFail,
  markAsReadSuccess
} from './notification-base.action';
import { NotificationBaseApi } from './notification-base.api';
import { UNREAD_NOTIFICATION_LIST_KEY } from './notification-base.constant';

@Injectable()
export class NotificationBaseEffect {

  private _setupUpdateStreamSubscription$ = createEffect(() => this._userEntity.activeClientId$.pipe(
    withLatestFrom(this._userEntity.activeUser$),
    filter(([ clientId, activeUser ]) => !!clientId && !!activeUser),
    map(([ clientId ]) => this._notificationEntity.updateSubscribe(clientId, {
      search: [
        {
          name: 'executorTag',
          operator: 'ne',
          value: 'L7_MASTER'
        }
      ],
      sort: [{
        name: 'created',
        ascending: false
      }]
    })
  )));

  private _onUpdateStreamMessage$ = createEffect(() => this._actions$.pipe(
    onWebsocketMessageDispatchUpdateEntities(this._notificationEntity)
  ));

  private _setupUnreadListStream$ = createEffect(() => this._userEntity.activeClientId$.pipe(
    withLatestFrom(this._userEntity.activeUser$),
    filter(([ clientId, activeUser ]) => !!clientId && !!activeUser),
    map(([ clientId, activeUser ]) => this._notificationEntity.listSubscribe(
      clientId,
      UNREAD_NOTIFICATION_LIST_KEY,
      {
        search: [
          {
            name: 'unreadByUserIds',
            operator: 'eq',
            value: activeUser.id
          },
          {
            name: 'executorTag',
            operator: 'ne',
            value: 'L7_MASTER'
          }
        ],
        sort: [{
          name: 'created',
          ascending: false
        }]
      }
    ))
  ));

  private _setupUnreadListAddRemove$ = createEffect(() => this._actions$.pipe(
    onWebsocketMessageDispatchAddRemoveEntities(
      this._notificationEntity,
      UNREAD_NOTIFICATION_LIST_KEY
    )
  ));

  private _onMarkAsRead$ = createEffect(() => this._actions$.pipe(
    ofType(markAsRead),
    mergeMap((action) => this._baseApi
      .markAsRead(action.data.id)
      .pipe(
        map(() => markAsReadSuccess({
          id: action.data.id
        }, action)),
        catchError((err) => of(markAsReadFail(err, action)))
      )
    )
  ));

  private _onMarkAsReadSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(markAsReadSuccess),
    map(({ data }) => this._notificationEntity.updateCache([ data ]))
  ));

  private _onMarkAllAsRead$ = createEffect(() => this._actions$.pipe(
    ofType(markAllAsRead),
    withLatestFrom(this._userEntity.activeClientId$),
    mergeMap(([ action, clientId ]) => this._baseApi
      .markAllAsRead(clientId, action.data?.serviceStackId, action.data?.projectId)
      .pipe(
        map((res) => markAllAsReadSuccess(res, action)),
        catchError((err) => of(markAllAsReadFail(err, action)))
      )
    )
  ));

  private _onMarkAllAsReadSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(markAllAsReadSuccess),
    withLatestFrom(this._notificationEntity.all$()),
    map(([ { originalAction }, notifications ]) => {
      const { projectId, serviceStackId } = originalAction.data;

      const updatedEntities = notifications.reduce((acc, itm) => {
        if (itm.acknowledged) return acc;

        const shouldUpdate = (!projectId && !serviceStackId)
          || (projectId && itm.project?.id === projectId)
          || (serviceStackId && itm.serviceStacks?.some(({ id }) => id === serviceStackId));

        if (shouldUpdate) {
          acc.push({ id: itm.id, acknowledged: true });
        }

        return acc;
      }, []);

      return this._notificationEntity.updateCache(updatedEntities);
    })
  ));

  constructor(
    private _actions$: Actions,
    private _notificationEntity: NotificationEntity,
    private _userEntity: UserEntity,
    private _baseApi: NotificationBaseApi
  ) { }
}
